import { useEffect, useRef, useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { trackPage } from "@/lib/analytics";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import SeoPage, { SEO_PAGES } from "@/pages/SeoPage";

const AgentDashboard = lazy(() => import("@/pages/AgentDashboard"));
const NewRequirement = lazy(() => import("@/pages/NewRequirement"));
const RequirementDetail = lazy(() => import("@/pages/RequirementDetail"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminRequirements = lazy(() => import("@/pages/AdminRequirements"));
const AdminRequirement = lazy(() => import("@/pages/AdminRequirement"));
const AdminWhatsApp = lazy(() => import("@/pages/AdminWhatsApp"));
const AdminAccounting = lazy(() => import("@/pages/AdminAccounting"));
const AdminAnalytics = lazy(() => import("@/pages/AdminAnalytics"));
const AdminAgents = lazy(() => import("@/pages/AdminAgents"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const Transfers = lazy(() => import("@/pages/Transfers"));
const NewTransfer = lazy(() => import("@/pages/NewTransfer"));
const TransferDetail = lazy(() => import("@/pages/TransferDetail"));
const AdminTransfers = lazy(() => import("@/pages/AdminTransfers"));
const AdminTransferDetail = lazy(() => import("@/pages/AdminTransferDetail"));
const TaxiTypes = lazy(() => import("@/pages/TaxiTypes"));
const Hotels = lazy(() => import("@/pages/Hotels"));
const MyBookings = lazy(() => import("@/pages/MyBookings"));
const NewHotelRequest = lazy(() => import("@/pages/NewHotelRequest"));
const HotelDetail = lazy(() => import("@/pages/HotelDetail"));
const AdminHotels = lazy(() => import("@/pages/AdminHotels"));
const AdminHotelDetail = lazy(() => import("@/pages/AdminHotelDetail"));
const AdminEmployees = lazy(() => import("@/pages/AdminEmployees"));
const EmployeeDashboard = lazy(() => import("@/pages/EmployeeDashboard"));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="page-loader">
    <div className="text-slate-400 text-sm">Loading…</div>
  </div>
);

const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const res = await api.post("/auth/session", { session_id: sessionId });
        window.history.replaceState({}, document.title, window.location.pathname);
        const user = res.data;
        navigate(user.role === "admin" ? "/admin" : user.role === "employee" ? "/employee" : user.profile_complete === false ? "/profile" : "/dashboard", { replace: true, state: { user } });
      } catch (e) {
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="auth-callback-loading">
      <div className="text-slate-600 text-sm">Signing you in…</div>
    </div>
  );
};

const ProtectedRoute = ({ children, role, needsProfile }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState(location.state?.user ? { status: "ok", user: location.state.user } : { status: "checking", user: null });

  useEffect(() => {
    if (location.state?.user) return;
    let alive = true;
    api.get("/auth/me")
      .then((res) => { if (alive) setState({ status: "ok", user: res.data }); })
      .catch(() => { if (alive) setState({ status: "no", user: null }); });
    return () => { alive = false; };
  }, [location.state]);

  useEffect(() => {
    if (state.status === "no") navigate("/login", { replace: true });
  }, [state.status, navigate]);

  if (state.status === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="auth-checking">
        <div className="text-slate-600 text-sm">Loading…</div>
      </div>
    );
  }
  if (state.status === "no") return null;
  if (role && state.user.role !== role) {
    return <Navigate to={state.user.role === "admin" ? "/admin" : state.user.role === "employee" ? "/employee" : "/dashboard"} replace />;
  }
  if (needsProfile && state.user.role === "travel_agent" && state.user.profile_complete === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6" data-testid="profile-incomplete-gate">
        <div className="bg-white border border-slate-200 rounded-2xl p-10 max-w-md text-center shadow-sm">
          <h1 className="font-heading text-xl font-bold text-slate-900">Profile Incomplete</h1>
          <p className="text-sm text-slate-500 mt-2 mb-6">Please complete your profile before proceeding.</p>
          <button data-testid="gate-complete-profile-btn" onClick={() => navigate("/profile")} className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-full px-6 py-2.5">
            Complete Your Profile
          </button>
        </div>
      </div>
    );
  }
  return children(state.user);
};

function AppRouter() {
  const location = useLocation();
  useEffect(() => { trackPage(location.pathname); }, [location.pathname]);
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      {Object.keys(SEO_PAGES).map((slug) => (
        <Route key={slug} path={`/${slug}`} element={<SeoPage slug={slug} />} />
      ))}
      <Route path="/login" element={<Login />} />
      <Route path="/dashboard" element={<ProtectedRoute>{(user) => <AgentDashboard user={user} />}</ProtectedRoute>} />
      <Route path="/new-requirement" element={<ProtectedRoute role="travel_agent" needsProfile>{(user) => <NewRequirement user={user} />}</ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute role="travel_agent">{(user) => <ProfilePage user={user} />}</ProtectedRoute>} />
      <Route path="/transfers" element={<ProtectedRoute role="travel_agent" needsProfile>{(user) => <Transfers user={user} />}</ProtectedRoute>} />
      <Route path="/transfers/new" element={<ProtectedRoute role="travel_agent" needsProfile>{(user) => <NewTransfer user={user} />}</ProtectedRoute>} />
      <Route path="/transfers/:id" element={<ProtectedRoute role="travel_agent">{(user) => <TransferDetail user={user} />}</ProtectedRoute>} />
      <Route path="/hotels" element={<ProtectedRoute role="travel_agent" needsProfile>{(user) => <Hotels user={user} />}</ProtectedRoute>} />
      <Route path="/hotels/new" element={<ProtectedRoute role="travel_agent" needsProfile>{(user) => <NewHotelRequest user={user} />}</ProtectedRoute>} />
      <Route path="/hotels/:id" element={<ProtectedRoute role="travel_agent">{(user) => <HotelDetail user={user} />}</ProtectedRoute>} />
      <Route path="/my-bookings" element={<ProtectedRoute role="travel_agent">{(user) => <MyBookings user={user} />}</ProtectedRoute>} />
      <Route path="/requirements/:id" element={<ProtectedRoute>{(user) => <RequirementDetail user={user} />}</ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute role="admin">{(user) => <AdminDashboard user={user} />}</ProtectedRoute>} />
      <Route path="/admin/requirements" element={<ProtectedRoute role="admin">{(user) => <AdminRequirements user={user} />}</ProtectedRoute>} />
      <Route path="/admin/requirements/:id" element={<ProtectedRoute role="admin">{(user) => <AdminRequirement user={user} />}</ProtectedRoute>} />
      <Route path="/admin/taxi-types" element={<ProtectedRoute role="admin">{(user) => <TaxiTypes user={user} />}</ProtectedRoute>} />
      <Route path="/admin/whatsapp" element={<ProtectedRoute role="admin">{(user) => <AdminWhatsApp user={user} />}</ProtectedRoute>} />
      <Route path="/admin/accounting" element={<ProtectedRoute role="admin">{(user) => <AdminAccounting user={user} />}</ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute role="admin">{(user) => <AdminAnalytics user={user} />}</ProtectedRoute>} />
      <Route path="/admin/agents" element={<ProtectedRoute role="admin">{(user) => <AdminAgents user={user} />}</ProtectedRoute>} />
      <Route path="/admin/transfers" element={<ProtectedRoute role="admin">{(user) => <AdminTransfers user={user} />}</ProtectedRoute>} />
      <Route path="/admin/transfers/:id" element={<ProtectedRoute role="admin">{(user) => <AdminTransferDetail user={user} />}</ProtectedRoute>} />
      <Route path="/admin/hotels" element={<ProtectedRoute role="admin">{(user) => <AdminHotels user={user} />}</ProtectedRoute>} />
      <Route path="/admin/hotels/:id" element={<ProtectedRoute role="admin">{(user) => <AdminHotelDetail user={user} />}</ProtectedRoute>} />
      <Route path="/admin/employees" element={<ProtectedRoute role="admin">{(user) => <AdminEmployees user={user} />}</ProtectedRoute>} />
      <Route path="/employee" element={<ProtectedRoute role="employee">{(user) => <EmployeeDashboard user={user} />}</ProtectedRoute>} />
      <Route path="/employee/agents/:aid" element={<ProtectedRoute role="employee">{(user) => <EmployeeDashboard user={user} />}</ProtectedRoute>} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
